import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '../../lib/api';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AdminPagination } from '../../components/AdminPagination';
import { AdminSearchBar } from '../../components/AdminSearchBar';

export const AdminVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    capacity_min: 1,
    capacity_max: 4,
    luggage_max: 2,
    image_url: '',
    features: '',
    is_active: true
  });

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getVehicles(page, 20, search);
      setVehicles(response.data.items);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Error al cargar vehículos');
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [page, search]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        capacity_min: parseInt(formData.capacity_min),
        capacity_max: parseInt(formData.capacity_max),
        luggage_max: parseInt(formData.luggage_max),
        features: formData.features.split(',').map(f => f.trim()).filter(Boolean)
      };
      
      if (editingVehicle) {
        await adminApi.updateVehicle(editingVehicle.id, data);
        toast.success('Vehículo actualizado exitosamente');
      } else {
        await adminApi.createVehicle(data);
        toast.success('Vehículo creado exitosamente');
      }
      
      setDialogOpen(false);
      setEditingVehicle(null);
      resetForm();
      loadVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Error al guardar el vehículo');
    }
  };
  
  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name,
      description: vehicle.description,
      capacity_min: vehicle.capacity_min,
      capacity_max: vehicle.capacity_max,
      luggage_max: vehicle.luggage_max,
      image_url: vehicle.image_url,
      features: vehicle.features.join(', '),
      is_active: vehicle.is_active
    });
    setDialogOpen(true);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este vehículo?')) return;
    
    try {
      await adminApi.deleteVehicle(id);
      toast.success('Vehículo eliminado exitosamente');
      loadVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Error al eliminar el vehículo');
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      capacity_min: 1,
      capacity_max: 4,
      luggage_max: 2,
      image_url: '',
      features: '',
      is_active: true
    });
  };
  
  if (loading && !hasLoadedRef.current) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-manrope font-bold text-3xl text-emerald-950">Vehículos</h1>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <AdminSearchBar value={search} onChange={handleSearch} placeholder="Buscar vehículo..." />
          <Button
            onClick={() => {
              resetForm();
              setEditingVehicle(null);
              setDialogOpen(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 shrink-0"
          >
            <Plus className="w-4 h-4" />
            Agregar
          </Button>
        </div>
      </div>
      
      <div className={`bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden transition-opacity ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Imagen</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Nombre</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Capacidad</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Equipaje</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-500">
                  {search ? `No se encontraron vehículos para "${search}"` : 'No hay vehículos registrados'}
                </td></tr>
              )}
              {vehicles.map((vehicle) => (
                <tr key={vehicle.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    {vehicle.image_url ? (
                      <img src={vehicle.image_url} alt={vehicle.name} className="w-16 h-16 object-cover rounded" />
                    ) : (
                      <div className="w-16 h-16 bg-emerald-50 rounded flex items-center justify-center text-emerald-600 text-xs font-medium">
                        Sin imagen
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{vehicle.name}</p>
                    <p className="text-sm text-slate-500">{vehicle.description}</p>
                  </td>
                  <td className="py-3 px-4 text-sm">{vehicle.capacity_min}-{vehicle.capacity_max}</td>
                  <td className="py-3 px-4 text-sm">{vehicle.luggage_max}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full
                      ${vehicle.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                      {vehicle.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => handleEdit(vehicle)}
                        className="p-2 hover:bg-slate-100 rounded transition-colors"
                      >
                        <Edit className="w-4 h-4 text-slate-600" />
                      </button>
                      <button 
                        onClick={() => handleDelete(vehicle.id)}
                        className="p-2 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVehicle ? 'Editar' : 'Agregar'} Vehículo</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Nombre</label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Descripción</label>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Cap. Mín</label>
                <Input 
                  type="number"
                  value={formData.capacity_min}
                  onChange={(e) => setFormData({...formData, capacity_min: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Cap. Máx</label>
                <Input 
                  type="number"
                  value={formData.capacity_max}
                  onChange={(e) => setFormData({...formData, capacity_max: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Equipaje</label>
                <Input 
                  type="number"
                  value={formData.luggage_max}
                  onChange={(e) => setFormData({...formData, luggage_max: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">URL de Imagen <span className="text-slate-400 font-normal">(opcional - se usará imagen por defecto si se deja vacío)</span></label>
              <Input 
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://... (dejar vacío para imagen por defecto)"
              />
              {formData.image_url && (
                <div className="mt-2 border rounded-lg overflow-hidden bg-slate-50" style={{maxHeight: '150px'}}>
                  <img 
                    src={formData.image_url} 
                    alt="Vista previa" 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  <div className="items-center justify-center gap-2 text-red-500 p-3 text-sm" style={{display: 'none'}}>
                    ⚠️ No se pudo cargar la imagen. Verifique la URL.
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Características (separadas por coma)</label>
              <Input 
                value={formData.features}
                onChange={(e) => setFormData({...formData, features: e.target.value})}
                placeholder="Aire acondicionado, WiFi, etc."
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Activo</label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {editingVehicle ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
